import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Briefcase,
  ClipboardList,
  Phone,
  Plus,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { Provider, ProviderRegistration, RegistrationStatus } from "@/types";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { PasswordInput } from "@/components/ui/PasswordInput";
import { Card, Badge } from "@/components/ui/Card";
import { Modal } from "@/components/ui/Modal";
import { ContactButtons } from "@/components/ContactButtons";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { TableSkeleton } from "@/components/ui/Skeleton";
import { ContactProviderModal } from "@/components/ContactProviderModal";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import { Pagination } from "@/components/ui/Pagination";

const createProviderSchema = z
  .object({
    nombre: z.string().min(2, "Mínimo 2 caracteres"),
    email: z.string().email("Email inválido"),
    companyName: z.string().min(2, "Mínimo 2 caracteres"),
    password: z.string().min(8, "Mínimo 8 caracteres"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Las contraseñas no coinciden",
    path: ["confirmPassword"],
  });
type CreateProviderForm = z.infer<typeof createProviderSchema>;

type ProviderSortKey = "nombre" | "clubs" | "active";
type RegistrationSortKey = "createdAt" | "companyName" | "status";

interface PaginatedResult<T> {
  data: T[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const selectClass =
  "rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20";

function SortHeader({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: "asc" | "desc";
  onClick: () => void;
}) {
  const Icon = !active ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 font-semibold transition-colors hover:text-ink-700 ${
        active ? "text-ink-700" : ""
      }`}
    >
      {label}
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

const registrationDateFormatter = new Intl.DateTimeFormat("es-ES", {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

export function ProvidersPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState<"proveedores" | "registros">("proveedores");

  // --- Proveedores ---
  const [providersPage, setProvidersPage] = useState(1);
  const [providersLimit, setProvidersLimit] = useState(10);
  const [nombreInput, setNombreInput] = useState("");
  const [apellidosInput, setApellidosInput] = useState("");
  const [nombreFilter, setNombreFilter] = useState("");
  const [apellidosFilter, setApellidosFilter] = useState("");
  const [activoFilter, setActivoFilter] = useState<"" | "true" | "false">("");
  const [sort, setSort] = useState<{ key: ProviderSortKey; dir: "asc" | "desc" }>({ key: "nombre", dir: "asc" });
  const [confirmProvider, setConfirmProvider] = useState<Provider | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createdNotice, setCreatedNotice] = useState<Provider | null>(null);

  // Espera a que se deje de escribir antes de volver a pedir al backend (si no, una petición por tecla).
  useEffect(() => {
    const timeout = setTimeout(() => {
      setNombreFilter(nombreInput);
      setApellidosFilter(apellidosInput);
      setProvidersPage(1);
    }, 300);
    return () => clearTimeout(timeout);
  }, [nombreInput, apellidosInput]);

  const { data: providersResp, isLoading } = useQuery({
    queryKey: ["providers", providersPage, providersLimit, nombreFilter, apellidosFilter, activoFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(providersPage), limit: String(providersLimit) });
      if (nombreFilter) params.set("nombre", nombreFilter);
      if (apellidosFilter) params.set("apellidos", apellidosFilter);
      if (activoFilter) params.set("activo", activoFilter);
      return (await api.get<PaginatedResult<Provider>>(`/providers?${params.toString()}`)).data;
    },
    placeholderData: keepPreviousData,
  });
  const providers = providersResp?.data ?? [];

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateProviderForm>({ resolver: zodResolver(createProviderSchema) });

  const createProvider = useMutation({
    mutationFn: async (data: CreateProviderForm) => {
      const { confirmPassword, ...payload } = data;
      return (await api.post<Provider>("/providers", payload)).data;
    },
    onSuccess: (data) => {
      reset();
      setCreateOpen(false);
      queryClient.invalidateQueries({ queryKey: ["providers"] });
      setCreatedNotice(data);
    },
    onError: (error: any) => toast.error(error?.response?.data?.error ?? "No se pudo crear el proveedor"),
  });


  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) => api.put(`/users/${id}`, { active }),
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ["providers"] });
      toast.success(vars.active ? "Proveedor activado" : "Proveedor desactivado");
    },
    onError: () => toast.error("No se pudo actualizar el proveedor"),
  });

  function toggleSort(key: ProviderSortKey) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  }

  // El filtrado (nombre/apellidos/estado) ya lo hace el backend — aquí solo se ordena la página actual.
  const visible = useMemo(() => {
    const dirMult = sort.dir === "asc" ? 1 : -1;
    return [...providers].sort((a, b) => {
      if (sort.key === "nombre") return a.nombre.localeCompare(b.nombre) * dirMult;
      if (sort.key === "clubs") return (a.clubs.length - b.clubs.length) * dirMult;
      return (Number(a.active) - Number(b.active)) * dirMult;
    });
  }, [providers, sort]);

  // --- Registros ---
  const [registrationsPage, setRegistrationsPage] = useState(1);
  const [registrationsLimit, setRegistrationsLimit] = useState(10);
  const [companyNameInput, setCompanyNameInput] = useState("");
  const [contactNameInput, setContactNameInput] = useState("");
  const [companyNameFilter, setCompanyNameFilter] = useState("");
  const [contactNameFilter, setContactNameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | RegistrationStatus>("");
  const [registrationSort, setRegistrationSort] = useState<{ key: RegistrationSortKey; dir: "asc" | "desc" }>({
    key: "createdAt",
    dir: "desc",
  });
  const [contacting, setContacting] = useState<ProviderRegistration | null>(null);
  const [approving, setApproving] = useState<ProviderRegistration | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCompanyNameFilter(companyNameInput);
      setContactNameFilter(contactNameInput);
      setRegistrationsPage(1);
    }, 300);
    return () => clearTimeout(timeout);
  }, [companyNameInput, contactNameInput]);

  const { data: registrationsResp, isLoading: registrationsLoading } = useQuery({
    queryKey: ["registrations", registrationsPage, registrationsLimit, companyNameFilter, contactNameFilter, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(registrationsPage), limit: String(registrationsLimit) });
      if (companyNameFilter) params.set("companyName", companyNameFilter);
      if (contactNameFilter) params.set("contactName", contactNameFilter);
      if (statusFilter) params.set("status", statusFilter);
      return (await api.get<PaginatedResult<ProviderRegistration>>(`/registrations?${params.toString()}`)).data;
    },
    placeholderData: keepPreviousData,
  });
  const registrations = registrationsResp?.data ?? [];

  // Independiente de la página/filtro que se esté viendo: cuenta el total real de pendientes.
  const { data: pendingCountRaw } = useQuery({
    queryKey: ["registrations", "pendingCount"],
    queryFn: async () =>
      (await api.get<PaginatedResult<ProviderRegistration>>("/registrations?status=PENDING&limit=1")).data.total,
  });
  const pendingCount = pendingCountRaw ?? 0;

  const approveMutation = useMutation({
    mutationFn: (id: number) => api.post(`/registrations/${id}/approve`),
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries({ queryKey: ["registrations"] });
      const clubText = `${data.clubs.length} club${data.clubs.length === 1 ? "" : "es"}`;
      if (data.emailSent) {
        toast.success(`Registro validado: se ${data.clubs.length === 1 ? "creó" : "crearon"} ${clubText} y se envió el correo de acceso.`);
      } else {
        toast.success(`Registro validado: se ${data.clubs.length === 1 ? "creó" : "crearon"} ${clubText}, pero no se pudo enviar el correo de acceso.`);
      }
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error ?? "No se pudo validar el registro");
    },
  });

  function toggleRegistrationSort(key: RegistrationSortKey) {
    setRegistrationSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  }

  // Igual que en Proveedores: el filtrado real (empresa/contacto/estado) ya lo hace el backend.
  const visibleRegistrations = useMemo(() => {
    const dirMult = registrationSort.dir === "asc" ? 1 : -1;
    return [...registrations].sort((a, b) => {
      if (registrationSort.key === "createdAt") return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dirMult;
      if (registrationSort.key === "companyName") return a.companyName.localeCompare(b.companyName) * dirMult;
      return a.status.localeCompare(b.status) * dirMult;
    });
  }, [registrations, registrationSort]);

  return (
    <div className="flex flex-col gap-8">
      <Breadcrumbs items={[{ label: "Proveedores" }]} />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span
            className={`flex h-12 w-12 items-center justify-center rounded-2xl ${
              tab === "proveedores" ? "bg-brand-50 text-brand-600" : "bg-violet-50 text-violet-600"
            }`}
          >
            {tab === "proveedores" ? <Briefcase className="h-6 w-6" /> : <ClipboardList className="h-6 w-6" />}
          </span>
          <div>
            <h1 className="text-2xl font-bold text-ink-900">Proveedores</h1>
            <p className="text-ink-400">
              {tab === "proveedores"
                ? "Empresas/franquicias con acceso al panel — cada una puede tener varios clubes."
                : `Solicitudes de clubes interesados en contratar PuntoPadel.${
                    pendingCount > 0 ? ` ${pendingCount} pendiente${pendingCount === 1 ? "" : "s"} de contactar.` : ""
                  }`}
            </p>
          </div>
        </div>
        {tab === "proveedores" && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            Crear proveedor
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {tab === "proveedores" ? (
          <>
            <Input
              placeholder="Nombre…"
              value={nombreInput}
              onChange={(e) => setNombreInput(e.target.value)}
              className="w-40"
            />
            <Input
              placeholder="Apellidos…"
              value={apellidosInput}
              onChange={(e) => setApellidosInput(e.target.value)}
              className="w-40"
            />
            <select
              value={activoFilter}
              onChange={(e) => {
                setActivoFilter(e.target.value as "" | "true" | "false");
                setProvidersPage(1);
              }}
              className={selectClass}
            >
              <option value="">Todos los estados</option>
              <option value="true">Activo</option>
              <option value="false">Inactivo</option>
            </select>
          </>
        ) : (
          <>
            <Input
              placeholder="Empresa…"
              value={companyNameInput}
              onChange={(e) => setCompanyNameInput(e.target.value)}
              className="w-40"
            />
            <Input
              placeholder="Contacto…"
              value={contactNameInput}
              onChange={(e) => setContactNameInput(e.target.value)}
              className="w-40"
            />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as "" | RegistrationStatus);
                setRegistrationsPage(1);
              }}
              className={selectClass}
            >
              <option value="">Todos los estados</option>
              <option value="PENDING">Pendiente</option>
              <option value="CONTACTED">Contactado</option>
              <option value="APPROVED">Aprobado</option>
            </select>
          </>
        )}
        <div className="flex rounded-xl border border-ink-100 bg-white p-1">
          <button
            onClick={() => setTab("proveedores")}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
              tab === "proveedores" ? "bg-brand-500 text-white" : "text-ink-500 hover:bg-ink-50"
            }`}
          >
            <Briefcase className="h-3.5 w-3.5" />
            Proveedores
          </button>
          <button
            onClick={() => setTab("registros")}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
              tab === "registros" ? "bg-brand-500 text-white" : "text-ink-500 hover:bg-ink-50"
            }`}
          >
            <ClipboardList className="h-3.5 w-3.5" />
            Registros
            {pendingCount > 0 && (
              <span
                className={`flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] ${
                  tab === "registros" ? "bg-white/20 text-white" : "bg-violet-100 text-violet-700"
                }`}
              >
                {pendingCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {tab === "proveedores" ? (
        <>
          <Card className="overflow-hidden p-0">
            {isLoading ? (
              <TableSkeleton rows={3} cols={6} />
            ) : !visible.length ? (
              <p className="p-6 text-sm text-ink-400">
                {nombreFilter || apellidosFilter || activoFilter
                  ? "Ningún proveedor coincide con los filtros."
                  : "Todavía no hay proveedores validados."}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead className="bg-ink-50/60 text-xs tracking-wide text-ink-400 uppercase">
                    <tr>
                      <th className="px-6 py-3">
                        <SortHeader label="Nombre" active={sort.key === "nombre"} dir={sort.dir} onClick={() => toggleSort("nombre")} />
                      </th>
                      <th className="px-6 py-3 font-semibold">Apellidos</th>
                      <th className="px-6 py-3 font-semibold">Email</th>
                      <th className="px-6 py-3">
                        <SortHeader label="Clubes" active={sort.key === "clubs"} dir={sort.dir} onClick={() => toggleSort("clubs")} />
                      </th>
                      <th className="px-6 py-3">
                        <SortHeader label="Estado" active={sort.key === "active"} dir={sort.dir} onClick={() => toggleSort("active")} />
                      </th>
                      <th className="px-6 py-3 font-semibold"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((provider) => (
                      <tr
                        key={provider.id}
                        onClick={() => navigate(`/proveedores/${provider.id}`)}
                        className="cursor-pointer border-t border-ink-100 transition-colors hover:bg-ink-50/50"
                      >
                        <td className="px-6 py-3.5 font-medium text-ink-800">{provider.nombre}</td>
                        <td className="px-6 py-3.5 text-ink-600">{provider.apellidos ?? "—"}</td>
                        <td className="px-6 py-3.5 text-ink-400">
                          <span className="flex items-center gap-2">
                            {provider.email}
                            <ContactButtons telefono={provider.telefono} email={provider.email} />
                          </span>
                        </td>
                        <td className="px-6 py-3.5">
                          <Badge tone={provider.clubs.length ? "green" : "slate"}>
                            {provider.clubs.length} club{provider.clubs.length === 1 ? "" : "es"}
                          </Badge>
                        </td>
                        <td className="px-6 py-3.5">
                          <Badge tone={provider.active ? "green" : "red"}>{provider.active ? "Activo" : "Inactivo"}</Badge>
                        </td>
                        <td className="px-6 py-3.5 text-right">
                          <Button
                            className="w-24"
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmProvider(provider);
                            }}
                          >
                            {provider.active ? "Desactivar" : "Activar"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
          <Pagination
            page={providersResp?.page ?? 1}
            totalPages={providersResp?.totalPages ?? 1}
            total={providersResp?.total ?? 0}
            onPageChange={setProvidersPage}
            limit={providersLimit}
            onLimitChange={(l) => {
              setProvidersLimit(l);
              setProvidersPage(1);
            }}
          />
        </>
      ) : (
        <>
          <Card className="overflow-hidden p-0">
            {registrationsLoading ? (
              <TableSkeleton rows={4} cols={5} />
            ) : !visibleRegistrations.length ? (
              <p className="p-6 text-sm text-ink-400">
                {companyNameFilter || contactNameFilter || statusFilter
                  ? "Ningún registro coincide con los filtros."
                  : "Todavía no hay solicitudes de registro."}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead className="bg-ink-50/60 text-xs tracking-wide text-ink-400 uppercase">
                    <tr>
                      <th className="px-6 py-3">
                        <SortHeader
                          label="Empresa"
                          active={registrationSort.key === "companyName"}
                          dir={registrationSort.dir}
                          onClick={() => toggleRegistrationSort("companyName")}
                        />
                      </th>
                      <th className="px-6 py-3 font-semibold">Contacto</th>
                      <th className="px-6 py-3">
                        <SortHeader
                          label="Recibido"
                          active={registrationSort.key === "createdAt"}
                          dir={registrationSort.dir}
                          onClick={() => toggleRegistrationSort("createdAt")}
                        />
                      </th>
                      <th className="px-6 py-3">
                        <SortHeader
                          label="Estado"
                          active={registrationSort.key === "status"}
                          dir={registrationSort.dir}
                          onClick={() => toggleRegistrationSort("status")}
                        />
                      </th>
                      <th className="px-6 py-3 font-semibold"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRegistrations.map((r) => (
                      <tr key={r.id} className="border-t border-ink-100 transition-colors hover:bg-ink-50/50">
                        <td className="px-6 py-3.5">
                          <p className="font-medium text-ink-800">{r.companyName}</p>
                          {r.city && <p className="text-xs text-ink-400">{r.city}</p>}
                        </td>
                        <td className="px-6 py-3.5 text-ink-500">
                          <p>{r.contactName}</p>
                          <p className="text-xs text-ink-400">{r.email}</p>
                        </td>
                        <td className="px-6 py-3.5 text-ink-500">{registrationDateFormatter.format(new Date(r.createdAt))}</td>
                        <td className="px-6 py-3.5">
                          <Badge tone={r.status === "APPROVED" ? "green" : "slate"}>
                            {r.status === "APPROVED" ? "Aprobado" : r.status === "CONTACTED" ? "Contactado" : "Pendiente"}
                          </Badge>
                        </td>
                        <td className="px-6 py-3.5 text-right">
                          <div className="flex justify-end gap-2">
                            <Button onClick={() => setContacting(r)}>
                              <Phone className="h-3.5 w-3.5" />
                              Contactar
                            </Button>
                            <Button className="w-28" onClick={() => setApproving(r)} disabled={r.status === "APPROVED"}>
                              <ShieldCheck className="h-3.5 w-3.5" />
                              {r.status === "APPROVED" ? "Validado" : "Validar"}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
          <Pagination
            page={registrationsResp?.page ?? 1}
            totalPages={registrationsResp?.totalPages ?? 1}
            total={registrationsResp?.total ?? 0}
            onPageChange={setRegistrationsPage}
            limit={registrationsLimit}
            onLimitChange={(l) => {
              setRegistrationsLimit(l);
              setRegistrationsPage(1);
            }}
          />
        </>
      )}

      {confirmProvider && (
        <ConfirmDialog
          open={!!confirmProvider}
          onOpenChange={(open) => !open && setConfirmProvider(null)}
          title={confirmProvider.active ? "Desactivar proveedor" : "Activar proveedor"}
          description={`¿Seguro que quieres ${confirmProvider.active ? "desactivar" : "activar"} a "${confirmProvider.nombre}"? ${
            confirmProvider.active
              ? "No podrá iniciar sesión (sus clubes y los usuarios de club siguen funcionando igual)."
              : ""
          }`}
          tone="primary"
          confirmLabel={confirmProvider.active ? "Desactivar" : "Activar"}
          onConfirm={() => toggleActive.mutate({ id: confirmProvider.id, active: !confirmProvider.active })}
        />
      )}

      <Modal
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) reset();
        }}
        title="Crear proveedor"
        description="Para cuando el proveedor ha contactado por otra vía (llamada, email) y no ha pasado por el formulario público de Registros."
      >
        <form onSubmit={handleSubmit((data) => createProvider.mutate(data))} className="flex flex-col gap-4">
          <Input id="provider-nombre" label="Nombre de contacto" error={errors.nombre?.message} {...register("nombre")} />
          <Input id="provider-email" type="email" label="Email" error={errors.email?.message} {...register("email")} />
          <Input
            id="provider-company"
            label="Nombre del club/empresa"
            error={errors.companyName?.message}
            {...register("companyName")}
          />
          <PasswordInput id="provider-password" label="Contraseña" error={errors.password?.message} {...register("password")} />
          <PasswordInput
            id="provider-confirm-password"
            label="Confirmar contraseña"
            error={errors.confirmPassword?.message}
            {...register("confirmPassword")}
          />
          <div className="flex justify-end gap-3">
            <Button type="button" onClick={() => setCreateOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={createProvider.isPending}>
              Crear proveedor
            </Button>
          </div>
        </form>
      </Modal>

      {createdNotice && (
        <Modal open onOpenChange={(open) => !open && setCreatedNotice(null)} title="Proveedor creado" description={createdNotice.nombre}>
          <div className="flex flex-col gap-4">
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <AlertTriangle className="mr-1.5 inline h-4 w-4 align-text-bottom" />
              No teníamos ninguna información de este proveedor procedente de un registro (teléfono, ciudad,
              mensaje) porque se ha dado de alta a mano — solo lo que has rellenado ahora mismo.
            </p>
            <p className="text-sm text-ink-500">
              Ya puede iniciar sesión con el email y la contraseña que le hayas indicado, y su primer club "
              {createdNotice.clubs[0]?.nombre}" ya está creado.
            </p>
            <div className="flex justify-end">
              <Button type="button" onClick={() => setCreatedNotice(null)}>
                Entendido
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {contacting && (
        <ContactProviderModal
          registration={contacting}
          open={!!contacting}
          onOpenChange={(open) => !open && setContacting(null)}
        />
      )}

      {approving && (
        <ConfirmDialog
          open={!!approving}
          onOpenChange={(open) => !open && setApproving(null)}
          title="Validar registro"
          description={`Se creará un club real y una cuenta de administrador para "${approving.companyName}", y se le enviará un correo con el acceso. Esta acción no se puede deshacer.`}
          confirmLabel="Validar"
          tone="primary"
          onConfirm={() => approveMutation.mutate(approving.id)}
        />
      )}
    </div>
  );
}
